<template>
  <div class="chat-container" :class="{ 'user-container': isUser }">
    <div v-if="!isUser" class="bot-circle">R</div>

    <div class="chat-bubbles">
      <div
        :class="[
          'chat-bubble-content',
          'chat-bubble',
          isUser
            ? 'user-message'
            : canBeCopied
            ? 'copyable-message'
            : 'bot-message',
        ]"
      >
        <div v-if="canBeCopied">
          <span v-if="options.length === 0" class="copyable-content">
            <span v-html="formattedText"></span>

            <b-icon
              :title="canBeCopied ? $t('chat_box.click_to_copy') : ''"
              icon="content-copy"
              class="copy-icon"
              @click.native="copyToClipboard(copyableText)"
            />
          </span>

          <span v-if="options.length" class="copyable-content-multiple">
            <span v-html="formattedText"></span>

            <span
              v-for="(option, index) in options"
              :key="index"
              class="copyable-options"
            >
              <span>{{ option }}</span>
              <b-icon
                :title="canBeCopied ? $t('chat_box.click_to_copy') : ''"
                icon="content-copy"
                class="copy-icon"
                @click.native="copyToClipboard(option)"
              />
            </span>
          </span>
        </div>

        <span v-else v-html="formattedText"></span>
      </div>

      <div
        v-if="hasDocuments"
        :class="[
          'chat-bubble',
          isUser ? 'user-message' : 'bot-message',
          'documents-bubble',
        ]"
      >
        <strong>{{ $t('chat_box.sources') }}</strong>
        <ul class="documents-list">
          <li v-for="(doc, idx) in normalizedDocuments" :key="docKey(doc, idx)">
            <a
              v-if="doc.presigned_url"
              class="document-link"
              :href="doc.presigned_url"
              :download="doc.title || 'document'"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ doc.title || 'Untitled document' }}
            </a>
            <span v-else>{{ doc.title || 'Untitled document' }}</span>
          </li>
        </ul>
      </div>

      <div v-if="canBeCopied && !isUser" class="feedback-buttons">
        <b-icon
          icon="thumb-up-outline"
          :class="[
            'feedback-icon',
            {
              active: feedbackGiven === 'POSITIVE',
              disabled: feedbackGiven && feedbackGiven !== 'POSITIVE',
            },
          ]"
          :title="$t('chat_box.thumbs_up')"
          @click.native="submitFeedback('POSITIVE')"
        />
        <b-icon
          icon="thumb-down-outline"
          :class="[
            'feedback-icon',
            {
              active: feedbackGiven === 'NEGATIVE',
              disabled: feedbackGiven && feedbackGiven !== 'NEGATIVE',
            },
          ]"
          :title="$t('chat_box.thumbs_down')"
          @click.native="submitFeedback('NEGATIVE')"
        />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import MarkdownIt from 'markdown-it';
// eslint-disable-next-line import/no-extraneous-dependencies
import DOMPurify from 'dompurify';

const md = new MarkdownIt({
  breaks: false,
  html: true,
  linkify: true,
});

// Remember old renderer, if overridden, or proxy to default renderer
const defaultRender =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  // If you are sure other plugins don't add `target` - dropped check
  const aIndex = tokens[idx].attrIndex('target');

  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']); // add new attribute
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank'; // replace value of existing attr
  }

  // Add rel="noopener noreferrer" for security
  const relIndex = tokens[idx].attrIndex('rel');
  if (relIndex < 0) {
    tokens[idx].attrPush(['rel', 'noopener noreferrer']);
  } else {
    tokens[idx].attrs[relIndex][1] = 'noopener noreferrer';
  }

  return defaultRender(tokens, idx, options, env, self);
};

export default {
  props: {
    text: {
      type: String,
      required: true,
    },
    copyText: {
      type: String,
      default: null,
    },
    documents: {
      type: Array,
      default: () => [],
      required: false,
    },
    isUser: {
      type: Boolean,
      required: true,
    },
    canBeCopied: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {
      feedbackGiven: null,
    };
  },
  computed: {
    copyableText() {
      return this.copyText == null ? this.text : this.copyText;
    },
    hasDocuments() {
      return Array.isArray(this.documents) && this.documents.length > 0;
    },
    normalizedDocuments() {
      return (this.documents || []).map((d) => ({
        title: d?.Title ?? d?.title ?? '',
        presigned_url: d?.presigned_url ?? d?.presignedUrl ?? d?.url ?? '',
      }));
    },
    formattedText() {
      const dirtyHtml = md.render(this.text);
      return DOMPurify.sanitize(dirtyHtml, { ADD_ATTR: ['target'] });
    },
  },
  methods: {
    submitFeedback(sentiment) {
      if (this.feedbackGiven) return;
      this.feedbackGiven = sentiment;
      this.$emit('feedback', sentiment);
    },
    docKey(doc, idx) {
      return (
        doc?.id ||
        doc?.key ||
        doc?.presigned_url ||
        `${doc?.title || 'doc'}-${idx}`
      );
    },
    copyToClipboard(text) {
      const content = text == null ? '' : String(text);

      if (!navigator?.clipboard?.writeText) {
        this.$buefy.toast.open({
          duration: 5000,
          message: 'Clipboard is not available in this browser/context.',
          type: 'is-warning',
        });
        return;
      }

      navigator.clipboard
        .writeText(content)
        .then(() => {
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Message copied to the clipboard!',
            type: 'is-info',
          });
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Failed to copy message to clipboard.',
            type: 'is-danger',
          });
        });
    },
  },
};
</script>

<style scoped>
.chat-bubble-content {
  display: flex;
  flex-direction: column;
  padding-left: 25px;
}

.copyable-options {
  display: flex;
  flex-direction: row;
}

.chat-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}

.user-container {
  flex-direction: row-reverse;
  width: 85%;
}

.bot-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background-color: #5a67d8;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border-radius: 50%;
  margin-right: 10px;
}

.user-container .bot-circle {
  margin-right: 0;
  margin-left: 10px;
}

.chat-bubbles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.chat-bubble {
  max-width: 95%;
  padding: 10px 15px;
  border-radius: 0 15px 15px 15px;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.chat-bubble:hover {
  background-color: #cfdcff;
}

.user-message {
  align-self: flex-end;
  background-color: #e6e8ff;
  border-radius: 15px 15px 0 15px;
  color: #333;
}

.bot-message {
  align-self: flex-start;
  background-color: #dfe9ff;
  color: #333;
}

.copyable-message {
  align-self: flex-start;
  background-color: #fff6e59b;
  border: 1px solid #ffe4b5;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyable-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.copyable-content-multiple {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.copy-icon {
  margin-left: auto;
  font-size: 16px;
  color: #ffa500;
}

.documents-bubble {
  cursor: default;
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
}

.documents-list {
  margin: 6px 0 0;
  padding-left: 18px;
  list-style-type: disc;
  list-style-position: outside;
}

.documents-list li {
  display: list-item;
}

.document-link {
  text-decoration: underline !important;
  color: #1f4aa8 !important;
  cursor: pointer;
}

.document-link:hover {
  color: #0d2f7a !important;
}

.chat-bubble :deep(a) {
  text-decoration: underline !important;
  color: #1f4aa8 !important;
}

.chat-bubble :deep(a:hover) {
  text-decoration: underline !important;
  color: #0d2f7a !important;
}

.chat-bubble :deep(h3) {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  margin: 18px 0 8px;
  padding: 4px 8px;
  background-color: #eef2ff;
  border-left: 3px solid #5a67d8;
  border-radius: 2px;
}

.chat-bubble :deep(h4) {
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
  margin: 12px 0 4px;
  padding-bottom: 2px;
  border-bottom: 1px solid #e2e8f0;
}

.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
  padding-left: 20px;
  margin: 4px 0;
  list-style: disc !important;
}

.chat-bubble :deep(ol) {
  list-style: decimal !important;
}

.chat-bubble :deep(ul ul),
.chat-bubble :deep(ol ul) {
  list-style: circle !important;
}

.chat-bubble :deep(li) {
  margin-bottom: 2px;
  display: list-item !important;
}

.chat-bubble :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0;
  font-size: 12px;
}

.chat-bubble :deep(th),
.chat-bubble :deep(td) {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: left;
}

.chat-bubble :deep(th) {
  background-color: #f0f0f0;
}

.chat-bubble :deep(a:hover) {
  text-decoration: underline !important;
  color: #0d2f7a !important;
}

.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
  padding-left: 20px;
  margin: 4px 0;
}

.chat-bubble :deep(li) {
  margin-bottom: 2px;
}

.chat-bubble :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0;
  font-size: 12px;
}

.chat-bubble :deep(th),
.chat-bubble :deep(td) {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: left;
}

.chat-bubble :deep(th) {
  background-color: #e8e8f0;
}

.feedback-buttons {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding-left: 4px;
}

.feedback-icon {
  font-size: 14px;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
}

.feedback-icon:hover {
  color: #5a67d8;
}

.feedback-icon.active {
  color: #5a67d8;
  cursor: default;
}

.feedback-icon.disabled {
  color: #ddd;
  cursor: default;
  pointer-events: none;
}

.feedback-buttons {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding-left: 4px;
}

.feedback-icon {
  font-size: 14px;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
}

.feedback-icon:hover {
  color: #5a67d8;
}

.feedback-icon.active {
  color: #5a67d8;
  cursor: default;
}

.feedback-icon.disabled {
  color: #ddd;
  cursor: default;
  pointer-events: none;
}
</style>
